import React, { useState, useEffect, useRef } from 'react';
import { Shield, CheckCircle, ArrowRight, Award, Star, ChevronDown, Clock, FileCheck, Users, Lock, ClipboardCheck, Settings, BadgeCheck, HelpCircle } from 'lucide-react';

// Intersection Observer Hook for scroll animations
const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  
  return [ref, inView];
};

// FAQ Accordion Component
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border border-slate-200 rounded-lg overflow-hidden">
    <button 
      onClick={onClick}
      className="w-full px-6 py-5 flex items-center justify-between bg-white hover:bg-slate-50 transition-colors text-left"
    >
      <span className="font-semibold text-slate-900 pr-4">{question}</span>
      <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    {isOpen && (
      <div className="px-6 pb-5 bg-white">
        <p className="text-slate-600 leading-relaxed">{answer}</p>
      </div>
    )}
  </div>
);

// ============================================
// CONFIGURATION
// ============================================
const API_BASE_URL = "https://bluegatesecurity.com";
// ============================================

export default function BlueGateSecurity() {
  const [openFAQ, setOpenFAQ] = useState(0);
  const [formData, setFormData] = useState({
    name: '', company: '', email: '', cmmcStatus: '', timeline: '', message: '', website_url: ''
  });
  const [formStatus, setFormStatus] = useState({ loading: false, success: false, error: '' });
  
  const [checklistEmail, setChecklistEmail] = useState('');
  const [checklistHoneypot, setChecklistHoneypot] = useState('');
  const [checklistStatus, setChecklistStatus] = useState({ loading: false, success: false, error: '' });

  const [heroRef, heroInView] = useInView();
  const [stakesRef, stakesInView] = useInView();
  const [servicesRef, servicesInView] = useInView();
  const [processRef, processInView] = useInView();
  const [leadRef, leadInView] = useInView();
  const [faqRef, faqInView] = useInView();
  const [contactRef, contactInView] = useInView();

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ loading: true, success: false, error: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/send-email.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setFormStatus({ loading: false, success: true, error: '' });
        setFormData({ name: '', company: '', email: '', cmmcStatus: '', timeline: '', message: '', website_url: '' });
      } else {
        setFormStatus({ loading: false, success: false, error: result.message || 'Something went wrong. Please try again.' });
      }
    } catch (error) {
      setFormStatus({ loading: false, success: false, error: 'Network error. Please check your connection and try again.' });
    }
  };

  const handleChecklistSubmit = async (e) => {
    e.preventDefault();
    setChecklistStatus({ loading: true, success: false, error: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/checklist-signup.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: checklistEmail, company_website: checklistHoneypot }),
      });

      const result = await response.json();

      if (result.success) {
        setChecklistStatus({ loading: false, success: true, error: '' });
        setChecklistEmail('');
        setChecklistHoneypot('');
      } else {
        setChecklistStatus({ loading: false, success: false, error: result.message || 'Something went wrong.' });
      }
    } catch (error) {
      setChecklistStatus({ loading: false, success: false, error: 'Network error. Please try again.' });
    }
  };

  const faqs = [
    {
      q: "What is CMMC and why do I need it?",
      a: "CMMC (Cybersecurity Maturity Model Certification) is a DoD requirement for contractors handling Controlled Unclassified Information (CUI). Starting in 2025, you'll need CMMC certification to bid on and win DoD contracts. Without it, you're ineligible—regardless of your past performance or pricing."
    },
    {
      q: "What's the difference between CMMC Level 1 and Level 2?",
      a: "Level 1 covers 17 basic cyber hygiene practices and allows self-assessment. Level 2 requires all 110 NIST 800-171 controls and third-party assessment by a C3PAO (Certified Third-Party Assessment Organization). Most contractors handling CUI will need Level 2."
    },
    {
      q: "How long does it take to get CMMC certified?",
      a: "Timeline varies based on your current security posture. Organizations with mature IT environments may achieve compliance in 6-9 months. Those starting from scratch typically need 12-18 months. Our assessment will give you a realistic timeline for your specific situation."
    },
    {
      q: "Can't our IT team handle this internally?",
      a: "While your IT team is essential to the process, CMMC compliance requires specialized expertise in DoD requirements, NIST controls, and C3PAO assessment expectations. Most internal IT teams aren't resourced for the documentation, evidence gathering, and ongoing monitoring CMMC demands."
    },
    {
      q: "What does your managed security service include?",
      a: "Our managed security service provides the continuous monitoring and maintenance CMMC requires—including log management, vulnerability scanning, incident response planning, and evidence collection for ongoing compliance. Think of it as your outsourced security operations team."
    },
    {
      q: "How much does CMMC compliance cost?",
      a: "Costs depend on your organization's size, current state, and target level. We provide transparent pricing after our initial assessment. What we can tell you: the cost of compliance is far less than the cost of losing DoD contracts or suffering a breach."
    }
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ fontFamily: "'Inter', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Merriweather:wght@700;900&display=swap');
        
        .font-serif { font-family: 'Merriweather', serif; }
        
        .navy { color: #1e3a5f; }
        .bg-navy { background-color: #1e3a5f; }
        .border-navy { border-color: #1e3a5f; }
        
        .gold { color: #b8960c; }
        .bg-gold { background-color: #b8960c; }
        .border-gold { border-color: #b8960c; }
        
        .btn-primary {
          background: linear-gradient(135deg, #1e3a5f 0%, #152a45 100%);
          transition: all 0.3s ease;
        }
        .btn-primary:hover {
          box-shadow: 0 8px 30px rgba(30, 58, 95, 0.3);
          transform: translateY(-2px);
        }
        
        .btn-gold {
          background: linear-gradient(135deg, #b8960c 0%, #9a7d0a 100%);
          transition: all 0.3s ease;
        }
        .btn-gold:hover {
          box-shadow: 0 8px 30px rgba(184, 150, 12, 0.3);
          transform: translateY(-2px);
        }
        
        .card-shadow {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 10px 30px -5px rgba(0, 0, 0, 0.1);
        }
        
        .card-shadow-lg {
          box-shadow: 0 10px 40px -10px rgba(30, 58, 95, 0.2);
        }
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-in {
          animation: fadeInUp 0.6s ease forwards;
        }
        
        .delay-1 { animation-delay: 0.1s; }
        .delay-2 { animation-delay: 0.2s; }
        .delay-3 { animation-delay: 0.3s; }
        .delay-4 { animation-delay: 0.4s; }
        
        input, select, textarea {
          transition: all 0.2s ease;
        }
        input:focus, select:focus, textarea:focus {
          outline: none;
          border-color: #1e3a5f;
          box-shadow: 0 0 0 3px rgba(30, 58, 95, 0.1);
        }
      `}</style>

      {/* Top Banner */}
      <div className="bg-navy text-white py-2.5 text-center text-sm">
        <div className="flex items-center justify-center gap-2">
          <Star className="w-4 h-4 text-yellow-400" fill="#facc15" />
          <span className="text-slate-300">Supporting U.S. Defense Contractors with CMMC Compliance</span>
          <Star className="w-4 h-4 text-yellow-400" fill="#facc15" />
        </div>
      </div>

      {/* Navigation */}
      <nav className="bg-white sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <a href="#" className="flex items-center gap-2">
                <Shield className="w-12 h-12 navy" />
                <span className="text-3xl font-bold">
                  <span className="navy">Blue</span><span className="gold">Gate</span>
                </span>
                <span className="text-xl text-slate-600 font-medium">Security</span>
              </a>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#services" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">Services</a>
              <a href="#process" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">Process</a>
              <a href="#faq" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">FAQ</a>
              <a href="#contact" className="btn-primary text-white px-6 py-2.5 rounded-lg font-semibold">
                Get Started
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section ref={heroRef} className="bg-gradient-to-b from-slate-50 to-white py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${heroInView ? 'animate-in' : 'opacity-0'}`}>
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Shield className="w-4 h-4" />
                CMMC Compliance Experts
              </div>
              
              <h1 className="font-serif text-4xl lg:text-5xl xl:text-6xl font-black navy leading-tight mb-6">
                Get CMMC Certified.
                <span className="gold block mt-1">Win DoD Contracts.</span>
              </h1>
              
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                We guide defense contractors through every step of CMMC compliance—from initial assessment to successful certification.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a href="#contact" className="btn-primary text-white px-8 py-4 rounded-lg font-semibold flex items-center justify-center gap-2 group">
                  Schedule Free Consultation
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
                <a href="#checklist" className="border-2 border-slate-300 hover:border-slate-400 text-slate-700 px-8 py-4 rounded-lg font-semibold transition-colors text-center">
                  Take Readiness Check
                </a>
              </div>

              <div className="flex items-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  Free Assessment
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  No Obligation
                </div>
              </div>
            </div>

            {/* Shield Visual */}
            <div className="relative flex justify-center">
              <div className="relative">
                {/* Main shield card */}
                <div className="bg-white rounded-2xl card-shadow-lg border border-slate-200 p-8 lg:p-10 relative z-10">
                  <div className="text-center">
                    {/* Shield badge */}
                    <div className="relative inline-block mb-6">
                      <div className="w-28 h-32 mx-auto">
                        <svg viewBox="0 0 100 115" className="w-full h-full">
                          <defs>
                            <linearGradient id="shieldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                              <stop offset="0%" stopColor="#1e3a5f" />
                              <stop offset="100%" stopColor="#152a45" />
                            </linearGradient>
                          </defs>
                          <path 
                            d="M50 2 L96 20 L96 55 Q96 95 50 113 Q4 95 4 55 L4 20 Z" 
                            fill="url(#shieldGrad)"
                            stroke="#b8960c"
                            strokeWidth="3"
                          />
                          <text x="50" y="50" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">CMMC</text>
                          <text x="50" y="72" textAnchor="middle" fill="#b8960c" fontSize="22" fontWeight="bold">L2</text>
                          <text x="50" y="90" textAnchor="middle" fill="white" fontSize="8">READY</text>
                        </svg>
                      </div>
                      <Star className="absolute -top-1 -right-1 w-6 h-6 text-yellow-500" fill="#eab308" />
                    </div>
                    
                    <h3 className="text-xl font-bold navy mb-2">Your Path to Certification</h3>
                    <p className="text-slate-500 mb-6">We handle the complexity so you can focus on your mission</p>
                    
                    <div className="grid grid-cols-3 gap-4 pt-6 border-t border-slate-200">
                      <div className="text-center">
                        <div className="text-2xl font-bold navy">110</div>
                        <div className="text-xs text-slate-500">Controls</div>
                      </div>
                      <div className="text-center border-x border-slate-200">
                        <div className="text-2xl font-bold navy">14</div>
                        <div className="text-xs text-slate-500">Domains</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold navy">1</div>
                        <div className="text-xs text-slate-500">Partner</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Decorative elements */}
                <div className="absolute -bottom-3 -left-3 w-16 h-16 border-l-4 border-b-4 border-yellow-500 rounded-bl-xl" />
                <div className="absolute -top-3 -right-3 w-16 h-16 border-r-4 border-t-4 border-yellow-500 rounded-tr-xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stakes Section - Why CMMC Matters */}
      <section ref={stakesRef} className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className={`text-center mb-12 ${stakesInView ? 'animate-in' : 'opacity-0'}`}>
            <h2 className="font-serif text-3xl lg:text-4xl font-black navy mb-4">
              Why CMMC Compliance Matters Now
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              The DoD is requiring CMMC certification for all contractors handling CUI. The clock is ticking.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Lock,
                title: "Contract Requirement",
                desc: "Starting in 2025, CMMC certification is mandatory for DoD contract eligibility. No certification means no contracts—period."
              },
              {
                icon: Shield,
                title: "Protect CUI",
                desc: "Safeguard Controlled Unclassified Information and meet federal security standards. Non-compliance puts national security at risk."
              },
              {
                icon: Award,
                title: "Competitive Advantage",
                desc: "Early compliance positions you ahead of competitors still scrambling to certify. Be ready when contracts drop."
              }
            ].map((item, i) => (
              <div 
                key={i} 
                className={`bg-slate-50 rounded-xl p-8 border border-slate-200 ${stakesInView ? 'animate-in' : 'opacity-0'}`}
                style={{ animationDelay: `${(i + 1) * 0.1}s` }}
              >
                <div className="w-12 h-12 bg-navy rounded-lg flex items-center justify-center mb-5">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-lg navy mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" ref={servicesRef} className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className={`text-center mb-12 ${servicesInView ? 'animate-in' : 'opacity-0'}`}>
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
              OUR SERVICES
            </div>
            <h2 className="font-serif text-3xl lg:text-4xl font-black navy mb-4">
              How We Help You Get Certified
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Two ways to work with us—choose consulting, managed security, or both.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Consulting Service */}
            <div className={`bg-white rounded-2xl card-shadow border border-slate-200 overflow-hidden ${servicesInView ? 'animate-in delay-1' : 'opacity-0'}`}>
              <div className="bg-navy p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
                    <ClipboardCheck className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">CMMC Consulting</h3>
                    <p className="text-slate-300 text-sm">Assessment to Certification</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <p className="text-slate-600 mb-6">
                  Our experts guide you through every step of the CMMC journey—identifying gaps, building your roadmap, and preparing you for C3PAO assessment.
                </p>
                <ul className="space-y-3 mb-6">
                  {[
                    "Comprehensive gap assessment against all 110 controls",
                    "System Security Plan (SSP) development",
                    "Policy and procedure documentation",
                    "Remediation planning and guidance",
                    "C3PAO assessment preparation"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <a href="#contact" className="inline-flex items-center gap-2 font-semibold navy hover:text-blue-700 transition-colors">
                  Learn more <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Managed Security Service */}
            <div className={`bg-white rounded-2xl card-shadow border border-slate-200 overflow-hidden ${servicesInView ? 'animate-in delay-2' : 'opacity-0'}`}>
              <div className="bg-gradient-to-r from-yellow-600 to-yellow-700 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
                    <Settings className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Managed Security</h3>
                    <p className="text-yellow-100 text-sm">Ongoing Compliance Operations</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <p className="text-slate-600 mb-6">
                  CMMC isn't a one-time checklist—it requires continuous monitoring. Our managed service handles the ongoing security operations your certification demands.
                </p>
                <ul className="space-y-3 mb-6">
                  {[
                    "24/7 security monitoring and log management",
                    "Continuous vulnerability scanning",
                    "Incident response planning and support",
                    "Ongoing evidence collection for audits",
                    "Quarterly compliance reviews"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <a href="#contact" className="inline-flex items-center gap-2 font-semibold gold hover:text-yellow-700 transition-colors">
                  Learn more <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" ref={processRef} className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className={`text-center mb-12 ${processInView ? 'animate-in' : 'opacity-0'}`}>
            <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
              OUR PROCESS
            </div>
            <h2 className="font-serif text-3xl lg:text-4xl font-black navy mb-4">
              Your Path to CMMC Certification
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              A clear, proven approach from assessment to certification.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Assess", desc: "Comprehensive evaluation of your current security posture against CMMC requirements", icon: FileCheck },
              { step: "02", title: "Plan", desc: "Custom roadmap with prioritized actions, timelines, and resource requirements", icon: ClipboardCheck },
              { step: "03", title: "Implement", desc: "Execute remediation with our guidance—policies, controls, and technical solutions", icon: Settings },
              { step: "04", title: "Certify", desc: "C3PAO assessment preparation and support through successful certification", icon: BadgeCheck }
            ].map((item, i) => (
              <div 
                key={i} 
                className={`relative ${processInView ? 'animate-in' : 'opacity-0'}`}
                style={{ animationDelay: `${(i + 1) * 0.1}s` }}
              >
                <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl font-black text-slate-400">{item.step}</span>
                    <div className="w-10 h-10 bg-navy rounded-lg flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <h3 className="font-bold text-lg navy mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
                {i < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-6 h-6 text-slate-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Magnet Section */}
      <section id="checklist" ref={leadRef} className="py-16 lg:py-20 bg-navy">
        <div className="max-w-4xl mx-auto px-6">
          <div className={`bg-white rounded-2xl card-shadow-lg overflow-hidden ${leadInView ? 'animate-in' : 'opacity-0'}`}>
            <div className="grid lg:grid-cols-2">
              <div className="p-8 lg:p-10">
                <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-full text-sm font-semibold mb-4">
                  <Clock className="w-4 h-4" />
                  FREE ASSESSMENT
                </div>
                <h3 className="font-serif text-2xl lg:text-3xl font-black navy mb-4">
                  The 10-Minute CMMC Readiness Check
                </h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Not sure where you stand? Take our quick self-assessment to identify your biggest compliance gaps and get a personalized readiness score.
                </p>
                <ul className="space-y-3 mb-6">
                  {[
                    "Evaluate your current security posture",
                    "Identify critical gaps in minutes",
                    "Get your personalized readiness score",
                    "Receive prioritized action items"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-slate-50 p-8 lg:p-10 flex items-center">
                {!checklistStatus.success ? (
                  <form onSubmit={handleChecklistSubmit} className="w-full">
                    <h4 className="font-semibold navy mb-4">Get instant access:</h4>
                    <input
                      type="email"
                      placeholder="Enter your work email"
                      value={checklistEmail}
                      onChange={(e) => setChecklistEmail(e.target.value)}
                      required
                      disabled={checklistStatus.loading}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg mb-4 disabled:bg-slate-100 disabled:cursor-not-allowed"
                    />
                    {/* Honeypot field - hidden from humans, catches bots */}
                    <input
                      type="text"
                      name="company_website"
                      value={checklistHoneypot}
                      onChange={(e) => setChecklistHoneypot(e.target.value)}
                      style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                    {checklistStatus.error && (
                      <p className="text-red-600 text-sm mb-4">{checklistStatus.error}</p>
                    )}
                    <button 
                      type="submit" 
                      disabled={checklistStatus.loading}
                      className="btn-gold w-full text-white py-4 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {checklistStatus.loading ? (
                        <>
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          Start Readiness Check
                          <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                    <p className="text-xs text-slate-500 mt-4 text-center">
                      No spam. We respect your inbox.
                    </p>
                  </form>
                ) : (
                  <div className="text-center w-full">
                    <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                    <h4 className="font-bold text-xl navy mb-2">Check Your Inbox!</h4>
                    <p className="text-slate-600">
                      Your readiness check is on its way. Check your email for the checklist!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" ref={faqRef} className="py-16 lg:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className={`text-center mb-12 ${faqInView ? 'animate-in' : 'opacity-0'}`}>
            <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <HelpCircle className="w-4 h-4" />
              FAQ
            </div>
            <h2 className="font-serif text-3xl lg:text-4xl font-black navy mb-4">
              Common Questions
            </h2>
            <p className="text-xl text-slate-600">
              Answers to what defense contractors ask us most.
            </p>
          </div>

          <div className={`space-y-4 ${faqInView ? 'animate-in delay-1' : 'opacity-0'}`}>
            {faqs.map((faq, i) => (
              <FAQItem
                key={i}
                question={faq.q}
                answer={faq.a}
                isOpen={openFAQ === i}
                onClick={() => setOpenFAQ(openFAQ === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" ref={contactRef} className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className={`text-center mb-10 ${contactInView ? 'animate-in' : 'opacity-0'}`}>
            <h2 className="font-serif text-3xl lg:text-4xl font-black navy mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-slate-600">
              Schedule your free consultation. We'll respond within 24 hours.
            </p>
          </div>

          <div className={`bg-white rounded-2xl card-shadow border border-slate-200 p-8 lg:p-10 ${contactInView ? 'animate-in delay-1' : 'opacity-0'}`}>
            {!formStatus.success ? (
              <form onSubmit={handleFormSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                      disabled={formStatus.loading}
                      placeholder="John Smith"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg disabled:bg-slate-100 disabled:cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Company *</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleFormChange}
                      required
                      disabled={formStatus.loading}
                      placeholder="Acme Defense Inc."
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg disabled:bg-slate-100 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Work Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    required
                    disabled={formStatus.loading}
                    placeholder="john@acmedefense.com"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg disabled:bg-slate-100 disabled:cursor-not-allowed"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Current CMMC Status *</label>
                    <select
                      name="cmmcStatus"
                      value={formData.cmmcStatus}
                      onChange={handleFormChange}
                      required
                      disabled={formStatus.loading}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-700 disabled:bg-slate-100 disabled:cursor-not-allowed"
                    >
                      <option value="">Select status</option>
                      <option value="not-started">Haven't started</option>
                      <option value="researching">Researching requirements</option>
                      <option value="in-progress">Remediation in progress</option>
                      <option value="audit-ready">Ready for assessment</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Target Timeline</label>
                    <select
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleFormChange}
                      disabled={formStatus.loading}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-700 disabled:bg-slate-100 disabled:cursor-not-allowed"
                    >
                      <option value="">Select timeline</option>
                      <option value="3-months">Within 3 months</option>
                      <option value="6-months">Within 6 months</option>
                      <option value="12-months">Within 12 months</option>
                      <option value="exploring">Just exploring</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Anything else we should know?</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleFormChange}
                    rows={4}
                    disabled={formStatus.loading}
                    placeholder="Tell us about your situation, current challenges, or specific questions..."
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg resize-none disabled:bg-slate-100 disabled:cursor-not-allowed"
                  />
                </div>

                {/* Honeypot field - hidden from humans, catches bots */}
                <input
                  type="text"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleFormChange}
                  style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
                  tabIndex="-1"
                  autoComplete="off"
                />

                {formStatus.error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {formStatus.error}
                  </div>
                )}

                <button 
                  type="submit" 
                  disabled={formStatus.loading}
                  className="btn-primary w-full text-white py-4 rounded-lg font-semibold flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {formStatus.loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      Request Free Consultation
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>

                <p className="text-xs text-slate-500 text-center">
                  By submitting, you agree to our Privacy Policy. We'll never share your information.
                </p>
              </form>
            ) : (
              <div className="text-center py-10">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-emerald-500" />
                </div>
                <h3 className="font-serif text-2xl font-bold navy mb-3">Thank You!</h3>
                <p className="text-slate-600 text-lg mb-6">
                  Your consultation request has been received.<br />
                  We'll be in touch within 24 hours.
                </p>
                <button 
                  onClick={() => setFormStatus({ loading: false, success: false, error: '' })}
                  className="text-navy font-semibold hover:underline"
                >
                  Submit another request
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mb-10">
            <div className="lg:col-span-2">
              <div className="mb-4">
                <a href="#" className="flex items-center gap-2">
                  <Shield className="w-12 h-12 text-white" />
                  <span className="text-3xl font-bold">
                    <span className="text-white">Blue</span><span className="gold">Gate</span>
                  </span>
                  <span className="text-xl text-slate-300 font-medium">Security</span>
                </a>
              </div>
              <p className="text-slate-400 mb-4 max-w-sm">
                Helping U.S. defense contractors achieve CMMC compliance and protect our nation's critical information.
              </p>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Shield className="w-4 h-4" />
                <span>Supporting U.S. Defense Contractors</span>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-white">Services</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">CMMC Consulting</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Gap Assessment</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Managed Security</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Audit Preparation</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-white">Contact</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li>info@bluegatesecurity.com</li>
                <li>Washington, D.C. Metro Area</li>
                <li className="pt-2">
                  <a href="#contact" className="inline-flex items-center gap-2 text-yellow-500 hover:text-yellow-400 transition-colors font-medium">
                    Get Started
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-700 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-500">
              © 2026 BlueGate Security. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <a href="#" className="hover:text-slate-300 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-slate-300 transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
